import _ from "lodash";
import React, { forwardRef } from "react";
import PropTypes from "prop-types";
import { AIPromptBox } from "@toddle-edu/ds-web";

import {
  MESSAGING_AI_ACTIONS,
  MESSAGING_AI_PROMPT_ACTIONS,
  getIsAIDebugModeEnabled,
  promptsIconMapping,
} from "MessagingV2/modules/Utils";
import AIFeedbackStrip from "AppComponents/AIFeedbackStrip";

import classes from "./AIPromptInput.scss";
import { useI18n, useMount } from "Hooks";

const {
  ELABORATE,
  SHORTEN,
  IMPROVE_WRITING,
  SIMPLIFY_WRITING,
  SPELLING_AND_GRAMMAR,
} = MESSAGING_AI_ACTIONS;

const {
  REPLACE,
  ADD_BELOW,
  KEEP,
  REGENERATE,
  DISCARD,
} = MESSAGING_AI_PROMPT_ACTIONS;

const DIVIDER = "divider";

const aiFeedBackStripContainerStyle = {
  borderRadius: "0px 0px 8px 8px",
};

const menuOptions = {
  [REPLACE]: {
    key: REPLACE,
    label: "progressReport:replace_selection",
    icon: promptsIconMapping[REPLACE],
  },
  [ADD_BELOW]: {
    key: ADD_BELOW,
    label: "progressReport:add_below",
    icon: promptsIconMapping[ADD_BELOW],
  },
  [KEEP]: {
    key: KEEP,
    label: "progressReport:keep",
    icon: promptsIconMapping[KEEP],
  },
  [REGENERATE]: {
    key: REGENERATE,
    label: "progressReport:regenerate",
    icon: promptsIconMapping[REGENERATE],
  },
  [DISCARD]: {
    key: DISCARD,
    label: "progressReport:discard",
    icon: promptsIconMapping[DISCARD],
  },
  [DIVIDER]: {
    key: DIVIDER,
    isDivider: true,
  },
  [SIMPLIFY_WRITING]: {
    key: SIMPLIFY_WRITING,
    label: "progressReport:simplify_language",
    icon: promptsIconMapping[SIMPLIFY_WRITING],
  },
  [IMPROVE_WRITING]: {
    key: IMPROVE_WRITING,
    label: "progressReport:improve_language",
    icon: promptsIconMapping[IMPROVE_WRITING],
  },
  [ELABORATE]: {
    key: ELABORATE,
    label: "progressReport:elaborate",
    icon: promptsIconMapping[ELABORATE],
  },
  [SHORTEN]: {
    key: SHORTEN,
    label: "common:shorten",
    icon: promptsIconMapping[SHORTEN],
  },
  [SPELLING_AND_GRAMMAR]: {
    key: SPELLING_AND_GRAMMAR,
    label: "progressReport:fix_spelling_and_grammer",
    icon: promptsIconMapping[SPELLING_AND_GRAMMAR],
  },
};

const STABLE_EMPTY_ARRAY = [];
// TODO: memo calculation
const getPromptBoxMenuOptions = ({ t, promptOptionsType }) => {
  switch (promptOptionsType) {
    case PROMPT_OPTIONS_GROUP_TYPE.GEN_COMPLETED:
      return _.map(
        [
          KEEP,
          REGENERATE,
          DISCARD,
          DIVIDER,
          SIMPLIFY_WRITING,
          ELABORATE,
          SHORTEN,
        ],
        key => ({
          ...menuOptions[key],
          label: t(menuOptions[key].label),
        })
      );
    case PROMPT_OPTIONS_GROUP_TYPE.ENHANCE_COMPLETED:
      return _.map(
        [
          REPLACE,
          // ADD_BELOW, // TODO: to find logic to support this
          REGENERATE,
          DISCARD,
          DIVIDER,
          SIMPLIFY_WRITING,
          ELABORATE,
          SHORTEN,
        ],
        key => ({
          ...menuOptions[key],
          label: t(menuOptions[key].label),
        })
      );
    case PROMPT_OPTIONS_GROUP_TYPE.ENHANCE:
      return _.map(
        [
          IMPROVE_WRITING,
          SIMPLIFY_WRITING,
          ELABORATE,
          SHORTEN,
          SPELLING_AND_GRAMMAR,
        ],
        key => ({
          ...menuOptions[key],
          label: t(menuOptions[key].label),
        })
      );
    default:
      return STABLE_EMPTY_ARRAY;
  }
};

export const PROMPT_OPTIONS_GROUP_TYPE = {
  GEN_COMPLETED: "GEN_COMPLETED",
  ENHANCE_COMPLETED: "ENHANCE_COMPLETED",
  ENHANCE: "ENHANCE",
};

const AIPromptInput = forwardRef(function AIPromptInput(props, ref) {
  const {
    onSubmit,
    isStreaming,
    onStop,
    onClickOutside,
    onOptionClick,
    textContent,
    placeholder,
    aiDebugObjRef,
    promptOptionsType,
  } = props;

  const { t } = useI18n(["common", "progressReport"]);

  useMount(() => {
    if (_.isFunction(ref.current?.focus)) ref.current.focus();
  });

  return (
    <div className={classes.container}>
      <div className={classes.innerContainer}>
        <AIPromptBox
          ref={ref}
          isStreaming={isStreaming}
          textContent={textContent}
          placeholder={placeholder}
          menuOptions={getPromptBoxMenuOptions({ t, promptOptionsType })}
          onStop={onStop}
          onOptionClick={onOptionClick}
          onSubmit={onSubmit}
          onClickOutside={onClickOutside}
          showFeedbackStrip={getIsAIDebugModeEnabled()}
          writing={t("common:writing_lowercase")}
          feedbackStrip={
            <AIFeedbackStrip
              {...aiDebugObjRef.current}
              containerStyle={aiFeedBackStripContainerStyle}
            />
          }
        />
      </div>
    </div>
  );
});

AIPromptInput.propTypes = {
  onStop: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onClickOutside: PropTypes.func.isRequired,
  isStreaming: PropTypes.bool,
  textContent: PropTypes.string,
  placeholder: PropTypes.string,
  promptOptionsType: PropTypes.oneOf(_.values(PROMPT_OPTIONS_GROUP_TYPE)),
};

AIPromptBox.defaultProps = {
  isStreaming: false,
  promptOptionsType: null,
  textContent: "",
};

export default AIPromptInput;
